import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  PlayArrow as PlayIcon,
  Publish as PublishIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { reelService, publishingService, socialService } from '../services/api';

interface PublishingResult {
  id: number;
  reel_id: number;
  platform: string;
  status: 'pending' | 'published' | 'failed' | 'scheduled';
  published_at?: string;
  scheduled_for?: string;
  platform_post_id?: string;
  error_message?: string;
  metrics?: {
    views?: number;
    likes?: number;
    shares?: number;
    comments?: number;
  };
}

interface Reel {
  id: number;
  title: string;
  status: string;
  video_url?: string;
  thumbnail_url?: string;
  project?: {
    id: number;
    name: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Publishing: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedReels, setCompletedReels] = useState<Reel[]>([]);
  const [publishingQueue, setPublishingQueue] = useState<any[]>([]);
  const [publishingHistory, setPublishingHistory] = useState<PublishingResult[]>([]);
  const [selectedReel, setSelectedReel] = useState<Reel | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  
  const [connectedAccounts, setConnectedAccounts] = useState<string[]>([]);

  // Publishing form data
  const [publishForm, setPublishForm] = useState({
    platforms: {
      tiktok: false,
      instagram: false,
      youtube: false,
    } as Record<string, boolean>,
    caption: '',
    hashtags: '',
    schedule_time: null as Date | null,
    cross_post: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch connected social accounts
      try {
        const accountsResponse = await socialService.accounts();
        const accounts = accountsResponse.data?.accounts || [];
        setConnectedAccounts(accounts.map((a: any) => a.platform));
      } catch {
        setConnectedAccounts([]);
      }

      // Fetch completed reels
      const reelsResponse = await reelService.list({ status: 'ready' });
      if (reelsResponse.data && reelsResponse.data.items) {
        setCompletedReels(reelsResponse.data.items);
      } else if (Array.isArray(reelsResponse.data)) {
        setCompletedReels(reelsResponse.data);
      }

      // Fetch publishing queue
      try {
        const queueResponse = await publishingService.queue();
        const queueData = queueResponse.data?.jobs || queueResponse.data?.items || [];
        setPublishingQueue(Array.isArray(queueData) ? queueData : []);
      } catch {
        setPublishingQueue([]);
      }

      // Fetch publishing history
      try {
        const queueResponse = await publishingService.queue();
        const allJobs = queueResponse.data?.jobs || [];
        setPublishingHistory(
          allJobs.filter((j: any) => j.status === 'published' || j.status === 'failed')
        );
      } catch {
        setPublishingHistory([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      enqueueSnackbar('Failed to load publishing data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePublishClick = (reel: Reel) => {
    setSelectedReel(reel);
    setPublishForm({
      platforms: {
        tiktok: false,
        instagram: false,
        youtube: false,
      },
      caption: reel.title,
      hashtags: '',
      schedule_time: null,
      cross_post: true,
    });
    setScheduleMode(false);
    setPublishDialogOpen(true);
  };

  const handlePublish = async () => {
    if (!selectedReel) return;
    
    const selectedPlatforms = Object.entries(publishForm.platforms)
      .filter(([_, selected]) => selected)
      .map(([platform]) => platform);
    
    if (selectedPlatforms.length === 0) {
      enqueueSnackbar('Please select at least one platform', { variant: 'warning' });
      return;
    }
    
    try {
      setLoading(true);
      
      if (scheduleMode && publishForm.schedule_time) {
        // Schedule for later
        await publishingService.schedule(selectedReel.id, {
          platforms: selectedPlatforms,
          caption: publishForm.caption,
          hashtags: publishForm.hashtags.split(' ').filter(tag => tag),
          scheduled_time: publishForm.schedule_time.toISOString(),
        });
        enqueueSnackbar('Reel scheduled successfully!', { variant: 'success' });
      } else {
        // Publish now
        await publishingService.publish({
          reel_id: selectedReel.id,
          platforms: selectedPlatforms,
          caption: publishForm.caption,
          hashtags: publishForm.hashtags.split(' ').filter(tag => tag),
          cross_post: publishForm.cross_post,
        });
        enqueueSnackbar('Publishing started!', { variant: 'success' });
      }
      
      setPublishDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to publish:', error);
      enqueueSnackbar('Failed to publish reel', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <InstagramIcon />;
      case 'facebook':
        return <FacebookIcon />;
      case 'twitter':
        return <TwitterIcon />;
      case 'youtube':
        return <YouTubeIcon />;
      case 'linkedin':
        return <LinkedInIcon />;
      default:
        return <PublishIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Publishing
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab label="Ready to Publish" />
            <Tab label="Publishing Queue" />
            <Tab label="Published" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : completedReels.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No reels ready to publish
                </Typography>
                <Typography color="text.secondary">
                  Complete reel processing first to publish them
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {completedReels && Array.isArray(completedReels) ? completedReels.map((reel) => (
                  <Grid item xs={12} sm={6} md={4} key={reel.id}>
                    <Card>
                      {reel.thumbnail_url && (
                        <CardMedia
                          component="img"
                          height="200"
                          image={reel.thumbnail_url}
                          alt={reel.title}
                        />
                      )}
                      <CardContent>
                        <Typography variant="h6" gutterBottom noWrap>
                          {reel.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {reel.project?.name || 'No Project'}
                        </Typography>
                        <Box mt={2}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PublishIcon />}
                            onClick={() => handlePublishClick(reel)}
                          >
                            Publish Now
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ScheduleIcon />}
                            onClick={() => {
                              handlePublishClick(reel);
                              setScheduleMode(true);
                            }}
                            sx={{ mt: 1 }}
                          >
                            Schedule
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )) : null}
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {publishingQueue.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No items in publishing queue
                </Typography>
                <Typography color="text.secondary">
                  Scheduled posts will appear here
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Reel</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Scheduled For</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publishingQueue && Array.isArray(publishingQueue) ? publishingQueue.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.reel_title}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getPlatformIcon(item.platform)}
                            {item.platform}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {format(new Date(item.scheduled_for), 'MMM d, yyyy h:mm a')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="small" color="error">
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {publishingHistory.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No publishing history
                </Typography>
                <Typography color="text.secondary">
                  Published reels will appear here with their performance metrics
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Reel</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Published</TableCell>
                      <TableCell>Views</TableCell>
                      <TableCell>Engagement</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Publishing history will be displayed here */}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Paper>
      </Box>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {scheduleMode ? 'Schedule Reel' : 'Publish Reel'}
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            {selectedReel && (
              <>
                <Typography variant="h6" gutterBottom>
                  {selectedReel.title}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                  Select Platforms
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(publishForm.platforms).map(([platform, selected]) => {
                    const isConnected = connectedAccounts.includes(platform);
                    return (
                      <Grid item xs={6} key={platform}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={selected}
                              disabled={!isConnected}
                              onChange={(e) => setPublishForm({
                                ...publishForm,
                                platforms: {
                                  ...publishForm.platforms,
                                  [platform]: e.target.checked,
                                },
                              })}
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              {getPlatformIcon(platform)}
                              {platform.charAt(0).toUpperCase() + platform.slice(1)}
                              {!isConnected && (
                                <Chip label="Not connected" size="small" color="default" variant="outlined" />
                              )}
                            </Box>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Caption"
                  value={publishForm.caption}
                  onChange={(e) => setPublishForm({ ...publishForm, caption: e.target.value })}
                  sx={{ mt: 3 }}
                />
                
                <TextField
                  fullWidth
                  label="Hashtags"
                  placeholder="#example #hashtags"
                  value={publishForm.hashtags}
                  onChange={(e) => setPublishForm({ ...publishForm, hashtags: e.target.value })}
                  sx={{ mt: 2 }}
                />
                
                {scheduleMode && (
                  <DateTimePicker
                    label="Schedule Time"
                    value={publishForm.schedule_time}
                    onChange={(newValue) => setPublishForm({ ...publishForm, schedule_time: newValue })}
                    minDateTime={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { mt: 2 }
                      }
                    }}
                  />
                )}
                
                {!scheduleMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={publishForm.cross_post}
                        onChange={(e) => setPublishForm({ ...publishForm, cross_post: e.target.checked })}
                      />
                    }
                    label="Cross-post to all selected platforms simultaneously"
                    sx={{ mt: 2 }}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePublish}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {scheduleMode ? 'Schedule' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Publishing;